import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { GATEWAY_HTTP_URL } from '../config/api';
import { PlanId, Plan, PLANS } from '../config/plans';

/**
 * Subscription data from the API
 */
export interface Subscription {
  id: string;
  planId: PlanId;
  planName: string;
  status: 'active' | 'canceled' | 'past_due' | 'paused';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  comped?: boolean;
}

/**
 * Usage statistics from the API
 */
export interface UsageStats {
  planId: PlanId;
  planName: string;
  minutesUsed: number;
  minutesLimit: number;
  minutesRemaining: number;
  percentUsed: number;
  periodStart: string;
  periodEnd: string;
  isOverLimit: boolean;
}

/**
 * Checkout data for Lemon Squeezy
 */
export interface CheckoutData {
  variantId: string;
  planId: PlanId;
  planName: string;
  customData: {
    userId: string;
    planId: PlanId;
  };
  customerEmail: string;
}

export function useSubscription() {
  const { getToken, isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch subscription and usage data
   */
  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !currentOrganization) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        'x-organization-id': currentOrganization.id
      };

      // Fetch subscription and usage in parallel
      const [subResponse, usageResponse] = await Promise.all([
        axios.get(`${GATEWAY_HTTP_URL}/api/billing/subscription`, { headers }),
        axios.get(`${GATEWAY_HTTP_URL}/api/billing/usage`, { headers }),
      ]);

      if (subResponse.data.success) {
        setSubscription(subResponse.data.subscription);
        setPlan(subResponse.data.plan);
      }

      if (usageResponse.data.success) {
        setUsage(usageResponse.data.usage);
      }
    } catch (err: any) {
      console.error('Failed to fetch subscription:', err);
      setError(err.response?.data?.error || 'Failed to load subscription');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getToken, currentOrganization]);

  useEffect(() => {
    if (currentOrganization) {
      fetchSubscription();
    }
  }, [fetchSubscription, currentOrganization]);

  /**
   * Get checkout data for upgrading to a plan
   */
  const getCheckoutData = useCallback(async (planId: PlanId): Promise<CheckoutData | null> => {
    if (!isAuthenticated || !currentOrganization) return null;

    try {
      const token = await getToken();
      const response = await axios.post(
        `${GATEWAY_HTTP_URL}/api/billing/checkout`,
        { planId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-organization-id': currentOrganization.id
          }
        }
      );

      if (response.data.success) {
        return response.data.checkout;
      }
      return null;
    } catch (err: any) {
      console.error('Failed to get checkout data:', err);
      setError(err.response?.data?.error || 'Failed to initiate checkout');
      return null;
    }
  }, [isAuthenticated, getToken, currentOrganization]);

  /**
   * Cancel subscription at end of billing period
   * @returns Object with success status and optional error message
   */
  const cancelSubscription = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!isAuthenticated || !currentOrganization) return { success: false, error: 'Not authenticated' };

    try {
      const token = await getToken();
      const response = await axios.post(
        `${GATEWAY_HTTP_URL}/api/billing/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-organization-id': currentOrganization.id
          }
        }
      );

      if (response.data.success) {
        // Update local state
        setSubscription(prev => prev ? {
          ...prev,
          cancelAtPeriodEnd: true,
        } : null);
        return { success: true };
      }
      return { success: false, error: 'Failed to cancel subscription' };
    } catch (err: any) {
      console.error('Failed to cancel subscription:', err);
      const errorMessage = err.response?.data?.error || 'Failed to cancel subscription';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [isAuthenticated, getToken, currentOrganization]);

  /**
   * Resume a canceled subscription
   */
  const resumeSubscription = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated || !currentOrganization) return false;

    try {
      const token = await getToken();
      const response = await axios.post(
        `${GATEWAY_HTTP_URL}/api/billing/resume`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-organization-id': currentOrganization.id
          }
        }
      );

      if (response.data.success) {
        // Update local state
        setSubscription(prev => prev ? {
          ...prev,
          cancelAtPeriodEnd: false,
        } : null);
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Failed to resume subscription:', err);
      setError(err.response?.data?.error || 'Failed to resume subscription');
      return false;
    }
  }, [isAuthenticated, getToken, currentOrganization]);

  /**
   * Check if current plan has a specific feature
   */
  const hasFeature = useCallback((feature: keyof Plan['features']): boolean => {
    if (!subscription) return false;
    const currentPlan = PLANS[subscription.planId];
    const value = currentPlan.features[feature];
    return typeof value === 'boolean' ? value : value > 0;
  }, [subscription]);

  /**
   * Check if user can upgrade to a specific plan
   */
  const canUpgradeTo = useCallback((planId: PlanId): boolean => {
    if (!subscription) return false;
    const currentPlan = PLANS[subscription.planId];
    const targetPlan = PLANS[planId];
    return targetPlan.priceMonthly > currentPlan.priceMonthly;
  }, [subscription]);

  return {
    subscription,
    usage,
    plan,
    isLoading,
    error,
    refresh: fetchSubscription,
    getCheckoutData,
    cancelSubscription,
    resumeSubscription,
    hasFeature,
    canUpgradeTo,
  };
}
